import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute from './components/ProtectedRoute';
import { NotificationProvider } from './contexts/NotificationContext';
import { AuthProvider } from './contexts/AuthContext';
import SiteLayout from './layouts/SiteLayout';
import HomePage from './pages/HomePage';

// Marketing pages are code-split per route; the hub/admin area keeps its own
// shell (PortalLayout) and never loads the marketing motion stack.
const WorkPage = lazy(() => import('./pages/WorkPage'));
const WorkDetailPage = lazy(() => import('./pages/WorkDetailPage'));
const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const IndustriesPage = lazy(() => import('./pages/IndustriesPage'));
const IndustryDetailPage = lazy(() => import('./pages/IndustryDetailPage'));
const AdsPage = lazy(() => import('./pages/AdsPage'));
const DemosPage = lazy(() => import('./pages/DemosPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const SetPasswordPage = lazy(() => import('./features/client/auth/SetPasswordPage'));
const ForgotPasswordPage = lazy(() => import('./features/client/auth/ForgotPasswordPage'));
const CheckoutResult = lazy(() => import('./pages/CheckoutResult'));
const PortalLayout = lazy(() => import('./layouts/PortalLayout'));
const HubPage = lazy(() => import('./features/hub/HubPage'));
const MvpDashboardPage = lazy(() => import('./features/mvp/MvpDashboardPage'));
const AdminProjectsPage = lazy(() => import('./features/admin/AdminProjectsPage'));
const AdminUsersPage = lazy(() => import('./features/admin/AdminUsersPage'));
const AdminPricingPage = lazy(() => import('./features/admin/AdminPricingPage'));
const AdminSettingsPage = lazy(() => import('./features/admin/AdminSettingsPage'));
const AdminLeadsPage = lazy(() => import('./features/admin/AdminLeadsPage'));
const AdminOrdersPage = lazy(() => import('./features/admin/AdminOrdersPage'));
const EngineLayout = lazy(() => import('./features/engine/EngineLayout'));
const CommandCenterPage = lazy(() => import('./features/engine/CommandCenterPage'));
const PipelinePage = lazy(() => import('./features/engine/PipelinePage'));
const ProspectsPage = lazy(() => import('./features/engine/ProspectsPage'));
const ProspectPage = lazy(() => import('./features/engine/ProspectPage'));
const ProposalEditorPage = lazy(() => import('./features/engine/studio/ProposalEditorPage'));
const CapabilitiesPage = lazy(() => import('./features/engine/CapabilitiesPage'));
const CampaignsPage = lazy(() => import('./features/engine/CampaignsPage'));
const ApprovalsPage = lazy(() => import('./features/engine/ApprovalsPage'));
const EngineSettingsPage = lazy(() => import('./features/engine/EngineSettingsPage'));
const AutopilotPage = lazy(() => import('./features/engine/AutopilotPage'));
const SalesDeskPage = lazy(() => import('./features/engine/SalesDeskPage'));
const JevAiPage = lazy(() => import('./features/engine/JevAiPage'));
const CalendarPage = lazy(() => import('./features/engine/CalendarPage'));
const HistoryPage = lazy(() => import('./features/engine/HistoryPage'));
const AnalyticsPage = lazy(() => import('./features/engine/AnalyticsPage'));

// Admin-only page inside the engine or the admin area; sales sees Access Denied.
const AdminOnly = ({ children }) => <ProtectedRoute requiredRole="admin">{children}</ProtectedRoute>;
const MissionControlPage = lazy(() => import('./features/engine/MissionControlPage'));

// Paths that render outside the marketing shell.
export const PORTAL_PREFIXES = ['/hub', '/admin', '/client-portal'];

function App() {
  return (
    <NotificationProvider>
      <Router>
        <AuthProvider>
          <Suspense fallback={<div className="min-h-screen bg-ink" />}>
            <Routes>
              <Route element={<SiteLayout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/work" element={<WorkPage />} />
                <Route path="/work/:slug" element={<WorkDetailPage />} />
                <Route path="/portfolio" element={<Navigate to="/work" replace />} />
                <Route path="/services" element={<ServicesPage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/industries" element={<IndustriesPage />} />
                <Route path="/industries/:slug" element={<IndustryDetailPage />} />
                <Route path="/ads" element={<AdsPage />} />
                <Route path="/demos" element={<DemosPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/set-password" element={<SetPasswordPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/checkout/success" element={<CheckoutResult />} />
                <Route path="/checkout/cancel" element={<CheckoutResult />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
              <Route path="/client-portal" element={<Navigate to="/hub/mvp" replace />} />
              <Route
                path="/hub"
                element={
                  <ProtectedRoute>
                    <PortalLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<HubPage />} />
                <Route path="mvp" element={<MvpDashboardPage />} />
              </Route>
              <Route
                path="/admin"
                element={
                  <ProtectedRoute requiredRole={['admin', 'sales']}>
                    <PortalLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="/admin/projects" replace />} />
                <Route path="projects" element={<AdminOnly><AdminProjectsPage /></AdminOnly>} />
                <Route path="users" element={<AdminOnly><AdminUsersPage /></AdminOnly>} />
                <Route path="pricing" element={<AdminOnly><AdminPricingPage /></AdminOnly>} />
                <Route path="settings" element={<AdminOnly><AdminSettingsPage /></AdminOnly>} />
                <Route path="leads" element={<AdminOnly><AdminLeadsPage /></AdminOnly>} />
                <Route path="orders" element={<AdminOnly><AdminOrdersPage /></AdminOnly>} />
              </Route>
              {/* The sales workspace has its own full-screen shell (light XRM theme), outside the hub chrome. */}
              <Route
                path="/admin/engine"
                element={
                  <ProtectedRoute requiredRole={['admin', 'sales']}>
                    <EngineLayout />
                  </ProtectedRoute>
                }
              >
                  <Route index element={<AdminOnly><CommandCenterPage /></AdminOnly>} />
                  <Route path="pipeline" element={<PipelinePage />} />
                  <Route path="prospects" element={<ProspectsPage />} />
                  <Route path="prospects/:id" element={<ProspectPage />} />
                  <Route path="campaigns" element={<AdminOnly><CampaignsPage /></AdminOnly>} />
                  <Route path="inbox" element={<ApprovalsPage />} />
                  <Route path="autopilot" element={<AdminOnly><AutopilotPage /></AdminOnly>} />
                  <Route path="sales-desk" element={<SalesDeskPage />} />
                  <Route path="proposals/:id" element={<ProposalEditorPage />} />
                  <Route path="jev" element={<JevAiPage />} />
                  <Route path="calendar" element={<CalendarPage />} />
                  <Route path="history" element={<HistoryPage />} />
                  <Route path="analytics" element={<AnalyticsPage />} />
                  <Route path="control" element={<AdminOnly><MissionControlPage /></AdminOnly>} />
                  <Route path="capabilities" element={<AdminOnly><CapabilitiesPage /></AdminOnly>} />
                  <Route path="settings" element={<EngineSettingsPage />} />
              </Route>
            </Routes>
          </Suspense>
        </AuthProvider>
      </Router>
    </NotificationProvider>
  );
}

export default App;
